import { useCallback } from 'react'
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'

import type { AgentId } from '@/config/env'

/**
 * Consolidates the 6 uniform URL-mutation sites across ConversationSidebar,
 * ConversationSidebarHeader, and AgentChat into named callbacks.
 *
 * Scope: 6 of 8 nav sites in the chat surface. The 2 sites NOT migrated:
 *  1. AgentChat error-recovery useEffect — deletes `conversationId` without
 *     setting `agent`, and only for a real "not found" (backend 404) with the
 *     thread not currently streaming; guarded by `params.has('conversationId')`
 *     against URL thrashing. Genuinely divergent shape; stays inline.
 *  2. ConversationSidebar.handleConversationClick's `if (!open) setOpen(true)`
 *     side effect remains at the call site after `goToConversation` returns.
 *     This hook does not own sidebar state.
 *
 * No-chaining contract: callbacks close over `useSearchParams()` at render
 * time. Two callbacks fired back-to-back within the SAME render both see
 * the pre-call searchParams — the first call's URL update is NOT visible
 * to the second. If chained updates are needed, combine them into one
 * callback or refactor the call site.
 *
 * Search emission: always guarded (`?foo=bar` or `''`, never bare `?`).
 * The original 6 sites split between guarded and unguarded forms, but all
 * 6 always set at least one param (agent), so the unification is
 * observationally a no-op for current callers.
 */
export function useConversationNav() {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()

  const navWith = useCallback(
    (
      update: (params: URLSearchParams) => void,
      agentId: AgentId,
      replace: boolean
    ) => {
      const params = new URLSearchParams(searchParams)
      update(params)
      params.set('agent', agentId)
      const search = params.toString()
      navigate(
        {
          pathname: location.pathname || `/${agentId}`,
          search: search ? `?${search}` : '',
        },
        { replace }
      )
    },
    [navigate, location.pathname, searchParams]
  )

  const goToConversation = useCallback(
    (agentId: AgentId, conversationId: string, replace = true) =>
      navWith(
        (p) => {
          p.delete('view')
          p.set('conversationId', conversationId)
        },
        agentId,
        replace
      ),
    [navWith]
  )

  const goToNewChat = useCallback(
    (agentId: AgentId, replace = true) =>
      navWith(
        (p) => {
          p.set('view', 'new')
          p.delete('conversationId')
        },
        agentId,
        replace
      ),
    [navWith]
  )

  const goToHistory = useCallback(
    (agentId: AgentId, replace = true) =>
      navWith(
        (p) => {
          p.set('view', 'history')
          // Preserve conversationId so back-from-history returns the user
          // to where they were instead of resetting to a new chat.
        },
        agentId,
        replace
      ),
    [navWith]
  )

  const clearConversation = useCallback(
    (agentId: AgentId, replace = true) =>
      navWith(
        (p) => {
          p.delete('conversationId')
          p.delete('view')
        },
        agentId,
        replace
      ),
    [navWith]
  )

  const clearActiveConversation = useCallback(
    (agentId: AgentId, replace = true) =>
      navWith(
        (p) => {
          // Drop only the conversation pointer; preserve `view` so deleting
          // the open chat from history keeps the user in the history tab.
          p.delete('conversationId')
        },
        agentId,
        replace
      ),
    [navWith]
  )

  return {
    goToConversation,
    goToNewChat,
    goToHistory,
    clearConversation,
    clearActiveConversation,
  }
}
